/**
 * CAPA Service – QMS Phase 5 + v2
 * Corrective and Preventive Actions: sourceType, actions[] (max 3), task generation.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'corrective_actions';
const SEQ_COLLECTION = 'org_sequences';

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function normalizeDueDate(d) {
  if (!d) return null;
  if (typeof d.toDate === 'function') return Timestamp.fromDate(d.toDate());
  if (typeof d.toMillis === 'function') return d;
  if (d instanceof Date) return Timestamp.fromDate(d);
  return Timestamp.fromDate(new Date(d));
}

export class CAPAService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  /**
   * Generează capa_code atomic via org_sequences.
   * Câmp: capa_last_seq. Format: CAPA-{YYYY}-{NNN}.
   */
  async _generateCapaCode(organizationId) {
    const seqRef = doc(db, SEQ_COLLECTION, organizationId);
    const seq = await runTransaction(db, async (tx) => {
      const snap = await tx.get(seqRef);
      const current = snap.exists() ? snap.data().capa_last_seq || 0 : 0;
      const next = current + 1;
      if (snap.exists()) {
        tx.update(seqRef, { capa_last_seq: next, updated_at: serverTimestamp() });
      } else {
        tx.set(seqRef, { capa_last_seq: next, updated_at: serverTimestamp() });
      }
      return next;
    });
    const year = new Date().getFullYear();
    return `CAPA-${year}-${String(seq).padStart(3, '0')}`;
  }

  /**
   * Creare CAPA v2.
   */
  async create(data, userId) {
    if (!data.sourceType) throw new Error('[CAPAService] sourceType este obligatoriu');
    if (!['rca', 'audit', 'fmea', 'deviation'].includes(data.sourceType)) {
      throw new Error(`[CAPAService] sourceType invalid: ${data.sourceType}`);
    }
    if (data.sourceType === 'deviation' && data.withoutRca && !data.rcaReason) {
      throw new Error('[CAPAService] rcaReason este obligatoriu când withoutRca: true');
    }
    if (data.sourceType === 'audit' && !data.auditFindingId && !data.auditId) {
      throw new Error('[CAPAService] auditFindingId sau auditId este obligatoriu pentru sourceType audit');
    }
    if (!data.actions || data.actions.length === 0) {
      throw new Error('[CAPAService] minimum o acțiune este obligatorie');
    }
    if (data.actions.length > 3) {
      throw new Error('[CAPAService] maximum 3 acțiuni permise per CAPA');
    }

    const capa_code = await this._generateCapaCode(data.organizationId);
    const now = Timestamp.now();

    const normalizedActions = data.actions.map((a) =>
      omitUndefined({
        actionId:
          a.actionId ||
          (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `action-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`),
        actionType: a.actionType || 'corrective',
        description: a.description || '',
        ownerId: a.ownerId || null,
        ownerName: a.ownerName || '',
        ownerGroupId: a.ownerGroupId || null,
        ownerGroupName: a.ownerGroupName || '',
        dueDate: normalizeDueDate(a.dueDate),
        status: 'open',
        taskInstanceId: null,
        protocolRef: a.protocolRef || null,
        completedAt: null,
        completedBy: null
      })
    );

    const payload = omitUndefined({
      capa_code,
      organizationId: data.organizationId,
      sourceType: data.sourceType,
      rcaId: data.sourceType === 'rca' ? data.rcaId || data.sourceId || null : null,
      auditId: data.sourceType === 'audit' ? data.auditId || null : null,
      auditFindingId: data.sourceType === 'audit' ? data.auditFindingId || data.sourceId || null : null,
      riskId: data.sourceType === 'fmea' ? data.riskId || null : null,
      deviationId: data.sourceType === 'deviation' ? data.deviationId || data.sourceId || null : null,
      sourceRef: data.sourceRef || null,
      withoutRca: data.withoutRca || false,
      rcaReason: data.withoutRca ? data.rcaReason || null : null,
      actions: normalizedActions,
      status: 'open',
      evidenceRefs: [],
      effectivenessVerification: data.effectivenessVerification || '',
      fmeaOScoreReviewFlagged: false,
      actionDescription: null,
      actionType: null,
      source: null,
      sourceId: null,
      evidence: null,
      assignedOwnerId: null,
      assignedOwnerName: '',
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      closedAt: null,
      closedBy: null
    });

    const ref = await addDoc(this.collectionRef, payload);

    this._generateTasksForActions(ref.id, capa_code, normalizedActions, data.organizationId, userId).catch(
      (e) => console.warn('[CAPAService] _generateTasksForActions failed (non-blocking):', e)
    );

    if (data.sourceType === 'rca' && payload.rcaId) {
      import('./rcaService')
        .then(({ rcaService }) => {
          rcaService.linkCAPA(payload.rcaId, ref.id, userId).catch((e) => console.warn('[CAPAService] linkCAPA on RCA failed:', e));
        })
        .catch((e) => console.warn('[CAPAService] rcaService import failed:', e));
    }

    if (data.sourceType === 'deviation' && payload.deviationId) {
      import('./deviationService')
        .then(({ deviationService }) => {
          deviationService.linkCorrectiveAction(payload.deviationId, ref.id, userId).catch((e) => console.warn('[CAPAService] linkCorrectiveAction failed:', e));
        })
        .catch((e) => console.warn('[CAPAService] deviationService import failed:', e));
    }

    try {
      await auditService.logCreate(COLLECTION, ref.id, capa_code, { sourceType: data.sourceType });
    } catch (e) {
      console.warn('[CAPAService] audit log failed:', e);
    }

    return ref.id;
  }

  async _generateTasksForActions(capaId, capaCode, actions, organizationId, userId) {
    for (const action of actions) {
      if (!action.ownerId && !action.ownerGroupId) {
        console.warn(`[CAPAService] action ${action.actionId} has no owner — skipping task generation`);
        continue;
      }

      try {
        const taskData = omitUndefined({
          name: action.description,
          type: 'capa_action',
          taskType: 'capa_action',
          status: 'assigned',
          triggeredBy: 'capa',
          source: 'capa',
          source_id: capaId,
          source_ref: capaCode,
          capa_action_id: action.actionId,
          actionType: action.actionType,
          assignedTo: action.ownerId || null,
          assignedToName: action.ownerName || null,
          assignedToGroup: action.ownerGroupId || null,
          assignedToGroupName: action.ownerGroupName || null,
          scheduledEnd: action.dueDate || null,
          organizationId,
          organization_id: organizationId,
          protocolRef: action.protocolRef || null,
          protocols: [],
          created_at: serverTimestamp(),
          created_by: userId,
          updated_at: serverTimestamp(),
          updated_by: userId
        });

        const taskRef = await addDoc(collection(db, 'task_instances'), taskData);

        const capaRef = doc(this.collectionRef, capaId);
        const capaSnap = await getDoc(capaRef);
        if (capaSnap.exists()) {
          const updatedActions = capaSnap.data().actions.map((a) =>
            a.actionId === action.actionId ? { ...a, taskInstanceId: taskRef.id } : a
          );
          await updateDoc(capaRef, {
            actions: updatedActions,
            updatedAt: Timestamp.now(),
            updatedBy: userId
          });
        }
      } catch (e) {
        console.warn(`[CAPAService] task generation failed for action ${action.actionId}:`, e);
      }
    }
  }

  async updateActionStatus(capaId, actionId, newStatus, userId) {
    const validStatuses = ['open', 'in_progress', 'verified', 'closed'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`[CAPAService] action status invalid: ${newStatus}`);
    }

    const ref = doc(this.collectionRef, capaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[CAPAService] CAPA not found');

    const data = snap.data();
    const now = Timestamp.now();

    const updatedActions = (data.actions || []).map((a) =>
      a.actionId === actionId
        ? {
            ...a,
            status: newStatus,
            completedAt: newStatus === 'verified' ? now : a.completedAt || null,
            completedBy: newStatus === 'verified' ? userId : a.completedBy || null
          }
        : a
    );

    const allVerified = updatedActions.every((a) => a.status === 'verified' || a.status === 'closed');
    const anyActive = updatedActions.some((a) => a.status === 'in_progress' || a.status === 'verified');
    const newCapaStatus = allVerified ? 'closed' : anyActive ? 'in_progress' : 'open';

    const patch = omitUndefined({
      actions: updatedActions,
      status: newCapaStatus,
      updatedAt: now,
      updatedBy: userId,
      closedAt: newCapaStatus === 'closed' ? now : undefined,
      closedBy: newCapaStatus === 'closed' ? userId : undefined,
      fmeaOScoreReviewFlagged: newCapaStatus === 'closed' ? true : undefined
    });

    await updateDoc(ref, patch);

    if (newCapaStatus === 'closed' && data.riskId) {
      import('./riskService')
        .then(({ riskService }) => {
          if (typeof riskService.flagOScoreReview === 'function') {
            riskService.flagOScoreReview(data.riskId, capaId).catch((e) => console.warn('[CAPAService] flagOScoreReview failed:', e));
          }
        })
        .catch((e) => console.warn('[CAPAService] riskService import failed:', e));
    }
  }

  async addEvidence(capaId, evidenceRef, userId) {
    const ref = doc(this.collectionRef, capaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[CAPAService] CAPA not found');
    const existing = snap.data().evidenceRefs || [];
    await updateDoc(ref, {
      evidenceRefs: [
        ...existing,
        {
          ...evidenceRef,
          uploadedAt: Timestamp.now(),
          uploadedBy: userId
        }
      ],
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async get(id) {
    const ref = doc(this.collectionRef, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async update(id, updates, userId) {
    const ref = doc(this.collectionRef, id);
    const current = await this.get(id);
    const patch = omitUndefined({
      ...updates,
      dueDate: updates.dueDate != null ? Timestamp.fromDate(new Date(updates.dueDate)) : undefined,
      completedAt: updates.status === 'completed' || updates.status === 'verified' ? Timestamp.now() : undefined,
      completedBy: updates.status === 'completed' || updates.status === 'verified' ? userId : undefined,
      verifiedAt: updates.status === 'verified' ? Timestamp.now() : undefined,
      verifiedBy: updates.status === 'verified' ? userId : undefined,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    await updateDoc(ref, patch);
    try {
      await auditService.logUpdate(COLLECTION, id, current?.actionDescription?.slice(0, 50) || current?.capa_code || id, {
        changedFields: Object.keys(updates)
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  async delete(id, userId) {
    const current = await this.get(id);
    const ref = doc(this.collectionRef, id);
    await deleteDoc(ref);
    try {
      await auditService.logDelete(COLLECTION, id, current?.actionDescription?.slice(0, 50) || current?.capa_code || id, {});
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  async listByOrganization(organizationId, filters = {}) {
    if (!organizationId) return [];
    const q = query(this.collectionRef, where('organizationId', '==', organizationId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    let items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (filters.source) {
      items = items.filter((i) => i.source === filters.source);
    }
    if (filters.actionType) {
      items = items.filter((i) => i.actionType === filters.actionType);
    }
    if (filters.status) {
      items = items.filter((i) => i.status === filters.status);
    }
    if (filters.sourceId) {
      items = items.filter((i) => i.sourceId === filters.sourceId);
    }
    if (filters.sourceType) {
      items = items.filter((i) => i.sourceType === filters.sourceType);
    }
    if (filters.rcaId) {
      items = items.filter((i) => i.rcaId === filters.rcaId);
    }
    if (filters.deviationId) {
      items = items.filter((i) => i.deviationId === filters.deviationId);
    }
    if (filters.overdueOnly) {
      const now = Date.now();
      items = items.filter((i) => {
        if (i.actions && Array.isArray(i.actions) && i.actions.length > 0) {
          return (
            i.status !== 'closed' &&
            i.actions.some((a) => {
              if (a.status === 'verified' || a.status === 'closed') return false;
              if (!a.dueDate) return false;
              const due = a.dueDate.toMillis?.() ?? 0;
              return due < now;
            })
          );
        }
        return (
          i.status !== 'completed' &&
          i.status !== 'verified' &&
          i.dueDate &&
          i.dueDate.toMillis?.() < now
        );
      });
    }
    return items;
  }
}

export const capaService = new CAPAService();
